import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router';

import useAccountQuery from 'api/queries/useAccountQuery';
import Alert from 'honeydew/Alert';

import { transformCustomTags } from './transformCustomTags';

/**
 * Filters a list of service delay banners according to the following constraints:
 *     - The banner is active and has no specified routes.
 *     - The banner is active and includes a route value matching the passed in pathname.
 *
 * @param {ServiceDelayBanner[]} banners - A list of service delay banners.
 * @param {string} pathname - The pathname to filter for.
 * @returns A filtered list of valid banners.
 */
const getValidBanners = (banners, pathname) =>
    Array.isArray(banners) &&
    banners.filter((banner) => {
        const isActive = banner.active;
        const hasNoRoutes = !banner.routes;
        const hasRouteMatchingPath = banner.routes?.some((route) => new RegExp(route).test(pathname));

        return isActive && (hasNoRoutes || hasRouteMatchingPath);
    });

function ServiceDelayBanners({ className }) {
    const location = useLocation();

    const { data: account } = useAccountQuery();

    const validBanners = getValidBanners(account?.serviceDelayBanners, location.pathname);
    const transformedBanners = transformCustomTags(validBanners);

    if (transformedBanners.length < 1) return false;

    return (
        <div className={clsx('flex flex-col gap-20', className)} data-testid="service-delay-banner-container">
            {transformedBanners.map((banner) => (
                <React.Fragment key={banner.id}>
                    <Alert variant="info-bell">
                        {/* eslint-disable-next-line react/no-danger */}
                        <span dangerouslySetInnerHTML={{ __html: banner.copy }} />
                    </Alert>
                </React.Fragment>
            ))}
        </div>
    );
}

export default ServiceDelayBanners;

ServiceDelayBanners.propTypes = {
    className: PropTypes.string,
};

ServiceDelayBanners.defaultProps = {
    className: null,
};
